import type { ComponentProps } from 'react';
import type MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { capitalizeFirstLetter } from './libs';

// One icon and one colour per expense category, so the Latest Expenses rows,
// the category donut and the yearly bars always agree. Keys are the server's
// fixed `bp_expense_type` values.

type IconName = ComponentProps<typeof MaterialCommunityIcons>['name'];

export const EXPENSE_CATEGORY_ICONS: Record<string, IconName> = {
  salary: 'account-cash-outline',
  stock: 'package-variant-closed',
  fuel: 'gas-station-outline',
  rent: 'home-city-outline',
  food: 'food-outline',
  transport: 'truck-outline',
  bill: 'file-document-outline',
  repairs: 'wrench-outline',
  advertising: 'bullhorn-outline',
  insurance: 'shield-check-outline',
  tax: 'bank-outline',
  loan: 'credit-card-outline',
  miscellaneous: 'dots-horizontal-circle-outline',
};

export const EXPENSE_CATEGORY_COLORS: Record<string, string> = {
  salary: '#2196F3',
  stock: '#FF9800',
  fuel: '#9C27B0',
  rent: '#4CAF50',
  food: '#E91E63',
  transport: '#00BCD4',
  bill: '#607D8B',
  repairs: '#FF5722',
  advertising: '#3F51B5',
  insurance: '#009688',
  tax: '#795548',
  loan: '#F44336',
  miscellaneous: '#9E9E9E',
};

export const getExpenseCategoryIcon = (type: string): IconName =>
  EXPENSE_CATEGORY_ICONS[type] || 'dots-horizontal-circle-outline';

export const getExpenseCategoryColor = (type: string, fallback: string): string =>
  EXPENSE_CATEGORY_COLORS[type] || fallback;

export type ExpenseCategoryShare = {
  type: string;
  label: string;
  amount: number;
  /** Share of the period's total, 0..1. */
  share: number;
};

/** Categories that took money in the period, largest first. */
export const getExpenseCategoryShares = (
  totalsByType?: Record<string, number> | null,
): ExpenseCategoryShare[] => {
  const rows = Object.entries(totalsByType || {})
    .map(([type, amount]) => ({ type, amount: Number(amount || 0) }))
    .filter(row => row.amount > 0)
    .sort((a, b) => b.amount - a.amount);
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  return rows.map(row => ({
    ...row,
    label: capitalizeFirstLetter(row.type),
    share: total > 0 ? row.amount / total : 0,
  }));
};

/**
 * Categories with spending in a year's monthly series, largest yearly total
 * first. The yearly chart stacks in this order and its legend lists it.
 */
export const getExpenseTypeOrder = (
  points: Array<{ totalsByType?: Record<string, number> }>,
): string[] => {
  const totals: Record<string, number> = {};
  (points || []).forEach(point => {
    Object.entries(point?.totalsByType || {}).forEach(([type, value]) => {
      totals[type] = (totals[type] || 0) + Number(value || 0);
    });
  });
  return Object.keys(totals)
    .filter(type => totals[type] > 0)
    .sort((a, b) => totals[b] - totals[a]);
};
